import { CommonModule, formatDate } from '@angular/common';
import { Component, ElementRef, HostListener, inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AuthService } from '@app/core/api/auth/auth.service';
import { SystemConfigurationService } from '@app/core/api/system-configuration/system-configuration.service';
import { InvestNarrativeResponse, InvestService } from '@app/core/api/users/invest.service';
import { FileUploadService } from '@app/core/api/files/file-upload.service';
import { ModalService } from '@app/shared/components/modal/modal.service';
import { ToastService } from '@app/shared/components/toast/toast.service';
import { ButtonComponent } from '@app/shared/forms/button/button.component';
import { InputComponent } from '@app/shared/forms/input/input.component';
import { QuillEditorComponent } from '@app/shared/forms/quill-editor/quill-editor.component';
import { SafeRichTextComponent } from '@app/shared/forms/safe-rich-text/safe-rich-text.component';
import { Loader, LucideAngularModule, SquarePen, View } from 'lucide-angular';
import { catchError, finalize, forkJoin, Observable, of, Subject, takeUntil, tap } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
    selector: 'app-profile',
    standalone: true,
    imports: [
        ButtonComponent,
        InputComponent,
        QuillEditorComponent,
        SafeRichTextComponent,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        LucideAngularModule,
    ],
    templateUrl: './profile.component.html',
    styleUrl: './profile.component.scss',
})
export class ProfileComponent implements OnInit, OnDestroy {
    authService = inject(AuthService);
    investService = inject(InvestService);
    modalService = inject(ModalService);
    toastService = inject(ToastService);
    systemConfigurationService = inject(SystemConfigurationService);
    fileUploadService = inject(FileUploadService);
    private sanitizer = inject(DomSanitizer);

    @ViewChild('updateProfileModal') updateProfileModal!: TemplateRef<any>;
    @ViewChild('cancelModalDetails') cancelModalDetails!: TemplateRef<any>;
    @ViewChild('confirmModalDetails') confirmModalDetails!: TemplateRef<any>;
    @ViewChild('mirror') mirror!: ElementRef;

    type: any;
    Math = Math;
    id: any;
    code: any;
    content: string = '';
    draft: string = '';
    lastUpdated: string = '';
    hasNarrative: boolean = false;
    description!: SafeHtml;
    isMobile = false;

    private profileLoaded = false;
    private reminderLoaded = false;

    destroy$ = new Subject<void>();

    form = new FormGroup({
        content: new FormControl(''),
    });

    agreed = false;
    loading: boolean = false;
    isEditing: boolean = false;
    isSubmitting: boolean = false;
    isOwner: boolean = false;
    isDraft: boolean = false;
    allUsers: any;

    readonly Loader = Loader;
    readonly SquarePen = SquarePen;
    readonly currentUser = this.authService.user();
    userProfile: any = [];

    profileUpdateRequired: boolean = false;
    lastProfileUpdated: string | null = null;
    nextUpdateDue: string | null = null;

    isLoadingDataPrivacy = false;
    profileAcknowledgement = '';

    ngOnInit(): void {
        this.id = this.currentUser?.invest_id;
        this.code = (this.currentUser?.type == 'Investor'
            ? this.currentUser?.registration_name
            : this.currentUser?.invest_code);
        this.type = this.currentUser?.type;

        this.form.disable();
        this.loadProfileAcknowledgement();

        forkJoin({
            profile: this.loadProfile(),
            reminder: this.getConfigReminder(),
        }).subscribe({
            next: () => {
                this.investService
                    .checkProfileUpdateRequired(this.id.toString())
                    .subscribe((res) => {
                        this.profileUpdateRequired = res.required;
                        this.lastProfileUpdated = res.lastUpdated;
                        this.nextUpdateDue = res.nextReminderDate;

                        if (this.profileUpdateRequired && this.profileLoaded && this.reminderLoaded && this.lastProfileUpdated != null) {
                            setTimeout(() => {
                                this.modal();
                            }, 100);
                        }
                    });
            },
        });
    }

    getConfigReminder(): Observable<any> {
        return this.investService.getAlertReminder().pipe(
            tap((response) => {
                const raw = response.message ?? '';
                const replaced = raw.replace(/&lt;EntityName&gt;|<EntityName>/g, this.code || '');
                this.description = this.sanitizer.bypassSecurityTrustHtml(replaced);
            }),
            finalize(() => {
                this.reminderLoaded = true;
            })
        );
    }

    modal() {
        const modal = this.modalService.open({
            title: 'Update Public Profile',
            contentTemplate: this.updateProfileModal,
            size: 'lg',
            contentData: {
                description: this.description,
                onConfirmRequest: () => {
                    this.modalService.close(modal);
                },
                onClose: () => {
                    this.investService
                        .updateProfileNarrative(this.id, {
                            latest: true
                        })
                        .pipe(takeUntil(this.destroy$))
                        .subscribe({
                            next: (response) => {
                                this.toastService.show({
                                    title: 'Success',
                                    content: response.message ?? 'Public profile updated successfully',
                                    type: 'success',
                                    duration: 5000,
                                });
                                this.modalService.close(modal);
                                this.profileUpdateRequired = false;
                                this.loadProfile();
                            },

                            error: (err) => {
                                let message = 'Something went wrong';

                                if (err?.error?.errors?.content) {
                                    message = err.error.errors.content.join(', ');
                                } else if (err?.error?.message) {
                                    message = err.error.message;
                                }

                                this.toastService.show({
                                    title: 'Error',
                                    content: message,
                                    type: 'error',
                                    duration: 3000,
                                    position: 'right',
                                });
                                this.modalService.close(modal);
                            },

                            complete: () => {
                                this.isSubmitting = false;
                                this.isEditing = false;
                                this.agreed = false;
                                this.loading = false;
                            },
                        });
                },
            },
            position: 'center',
            animation: 'fade',
            showCloseButton: false,
            backdropDismiss: false,
            keyboardClose: false
        });
    }

    loadProfile(): Observable<any> {
        this.loading = true;

        return this.investService.getInvestProfile(this.id).pipe(
            tap((res: InvestNarrativeResponse) => {
                this.userProfile = res.data;
                this.isOwner = this.currentUser?.invest_id === res.data.invest_entity_id;
                this.isDraft = this.userProfile?.is_draft;

                this.lastUpdated = this.userProfile?.updated_at
                    ? formatDate(this.userProfile.updated_at, 'MMMM dd, yyyy', 'en-US', 'UTC')
                    : this.userProfile?.created_at
                        ? formatDate(this.userProfile.created_at, 'MMMM dd, yyyy', 'en-US', 'UTC')
                        : '--';

                this.draft = this.userProfile?.draft;
                this.content = this.userProfile?.content;
                this.form.get('content')?.setValue(this.content ?? 'NO PUBLIC PROFILE');
                this.hasNarrative = !!this.content;

                if (!this.isOwner) {
                    this.form.get('content')?.disable();
                }
                this.getConfigReminder().subscribe();
                this.loading = false;
            }),
            catchError((error) => {
                const createdAt = error.error.details?.created_at;
                const updatedAt = error.error.details?.updated_at;

                this.lastUpdated =
                    formatDate(updatedAt, 'MMMM dd, yyyy', 'en-US', 'UTC') ??
                    formatDate(createdAt, 'MMMM dd, yyyy', 'en-US', 'UTC');

                this.form.get('content')?.setValue('NO PUBLIC PROFILE');
                this.hasNarrative = !!this.content;
                this.loading = false;
                return of(null);
            }),
            finalize(() => {
                this.loading = false;
                this.profileLoaded = true;
            })
        );
    }

    startEditing() {
        this.isEditing = true;
        this.agreed = false;
        this.form.enable();

        if (this.isEditing && this.isDraft) {
            this.form.get('content')?.setValue(this.draft);
        } else {
            this.form.get('content')?.setValue(this.content ?? 'NO PUBLIC PROFILE');
        }
    }

    updateNarrative(action: 'cancel' | 'draft' | 'final') {
        console.log(action);

        if (action === 'cancel') {
            this.showCancelModal();
            return;
        }

        if (!this.agreed) {
            this.toastService.show({
                title: 'Error',
                content: 'You must agree to the privacy policy before saving.',
                type: 'error',
                duration: 3000,
            });
            return;
        }

        if (!this.form.valid) {
            this.toastService.show({
                title: 'Error',
                content: 'The content field is required.',
                type: 'error',
                duration: 3000,
            });
            return;
        }

        let contentValue: string | null = this.form.get('content')?.value ?? null;

        if (!contentValue || contentValue.trim() === '' || contentValue === '<p><br></p>') {
            this.toastService.show({
                title: 'Error',
                content: 'Content cannot be empty.',
                type: 'error',
                duration: 3000,
            });
            return;
        }

        this.showConfirmModal(action, contentValue);
    }

    onTextChange(event: any) {
        console.log('HTML:', event.html);
        console.log('Text:', event.text);
        console.log('Delta:', event.delta);
    }

    showCancelModal() {
        const modal = this.modalService.open({
            title: 'Cancel Updating',
            contentTemplate: this.cancelModalDetails,
            contentData: {
                onCancel: () => {
                    this.modalService.close(modal);
                    this.onCancel();
                },
            },
            showCloseButton: false,
            backdropDismiss: false,
            keyboardClose: false
        });
    }

    showConfirmModal(action: 'draft' | 'final', contentValue: string) {
        const modal = this.modalService.open({
            title: action === 'final' ? 'Confirm Submit' : 'Confirm Save as Draft',
            contentTemplate: this.confirmModalDetails,
            contentData: {
                onConfirm: () => {
                    this.onConfirm(modal, action, contentValue);
                },
                isDraft: action === 'draft',
            },
            showCloseButton: false,
            keyboardClose: false,
            backdropDismiss: false,
        });
    }

    onCancel() {
        this.isEditing = false;
        this.agreed = false;
        this.form.disable();

        if (this.isEditing && this.isDraft) {
            this.form.get('content')?.setValue(this.draft);
        } else {
            this.form.get('content')?.setValue(this.content ?? 'NO PUBLIC PROFILE');
        }
        this.loading = false;
    }

    onConfirm(modalId: number, action: 'draft' | 'final', contentValue: string) {
        let sanitizedContent = contentValue.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '');
        sanitizedContent = sanitizedContent.replace(/ on\w+="[^"]*"/gi, '');

        // Extract base64 images from content
        const base64Images = this.extractBase64Images(sanitizedContent);

        if (base64Images.length > 0) {
            // Validate image count
            if (base64Images.length > 5) {
                this.toastService.show({
                    title: 'Error',
                    content: 'Cannot upload more than 5 images.',
                    type: 'error',
                    duration: 3000,
                });
                this.form.enable();
                this.modalService.close(modalId);
                return;
            }

            // Validate image sizes
            const validationErrors = this.validateImageSizes(base64Images);
            if (validationErrors.length > 0) {
                this.toastService.show({
                    title: 'Error',
                    content: validationErrors.join(', '),
                    type: 'error',
                    duration: 3000,
                });
                this.form.enable();
                this.modalService.close(modalId);
                return;
            }

            this.isSubmitting = true;
            this.form.disable();

            // Convert base64 to files
            const filePromises = base64Images.map((img, index) =>
                this.convertBase64ToFile(img.base64, `narrative-image-${index}.${img.extension}`, img.mimeType)
            );

            Promise.all(filePromises)
                .then((files) => {
                    // Create FormData with images and content in single request
                    const formData = new FormData();
                    formData.append('content', sanitizedContent);
                    formData.append('is_draft', action === 'draft' ? '1' : '0');

                    files.forEach((file, index) => {
                        formData.append(`images[${index}]`, file);
                    });

                    // Send single request with both content and images
                    return this.investService.updateProfileNarrativeWithImages(this.id, formData).toPromise();
                })
                .then(() => {
                    this.toastService.show({
                        title: 'Success',
                        content: `Public profile ${action == 'draft' ? 'saved as draft' : 'updated'} successfully`,
                        type: 'success',
                        duration: 5000,
                    });
                    this.modalService.close(modalId);
                    this.loadProfile().subscribe();;
                })
                .catch((err) => {
                    let message = 'Something went wrong';

                    if (err?.error?.errors?.content) {
                        message = err.error.errors.content.join(', ');
                    } else if (err?.error?.errors?.images) {
                        message = err.error.errors.images.join(', ');
                    } else if (err?.error?.message) {
                        message = err.error.message;
                    }

                    this.toastService.show({
                        title: 'Error',
                        content: message,
                        type: 'error',
                        duration: 3000,
                        position: 'right',
                    });

                    this.modalService.close(modalId);
                    this.form.enable();
                    this.isEditing = true;
                })
                .finally(() => {
                    this.isSubmitting = false;
                    this.isEditing = false;
                    this.agreed = false;
                });
        } else {
            // No images, proceed with normal update
            this.isSubmitting = true;
            this.form.disable();

            this.investService
                .updateProfileNarrative(this.id, {
                    content: sanitizedContent,
                    is_draft: action === 'draft',
                })
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (response) => {
                        this.toastService.show({
                            title: 'Success',
                            content: response.message ?? `Public profile ${action == 'draft' ? 'saved as draft' : 'updated'} successfully`,
                            type: 'success',
                            duration: 5000,
                        });
                        this.modalService.close(modalId);
                        this.loadProfile().subscribe();;
                    },

                    error: (err) => {
                        let message = 'Something went wrong';

                        if (err?.error?.errors?.content) {
                            message = err.error.errors.content.join(', ');
                        } else if (err?.error?.message) {
                            message = err.error.message;
                        }

                        this.toastService.show({
                            title: 'Error',
                            content: message,
                            type: 'error',
                            duration: 5000,
                            position: 'right',
                        });

                        this.form.enable();
                        this.isEditing = true;
                        this.isSubmitting = false;
                        this.modalService.close(modalId);
                    },

                    complete: () => {
                        this.isSubmitting = false;
                        this.isEditing = false;
                        this.agreed = false;
                        this.loading = false;
                    },
                });
        }
    }

    /**
     * Extract base64 encoded images from HTML content
     */
    private extractBase64Images(
        html: string
    ): Array<{ fullMatch: string; base64: string; mimeType: string; extension: string }> {
        const imgRegex = /<img[^>]+src=["']data:image\/([^;]+);base64,([^"']+)["'][^>]*>/gi;
        const matches: Array<{ fullMatch: string; base64: string; mimeType: string; extension: string }> = [];
        let match;

        while ((match = imgRegex.exec(html)) !== null) {
            matches.push({
                fullMatch: match[0],
                base64: match[2],
                mimeType: `image/${match[1]}`,
                extension: match[1],
            });
        }

        return matches;
    }

    /**
     * Validate image sizes (max 5MB per image)
     */
    private validateImageSizes(images: Array<{ base64: string }>): string[] {
        const errors: string[] = [];
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes

        images.forEach((img, index) => {
            // Calculate base64 file size (base64 is ~4/3 the original size)
            const sizeInBytes = (img.base64.length * 3) / 4;

            if (sizeInBytes > maxSize) {
                errors.push(`Image ${index + 1} exceeds 5MB limit (${(sizeInBytes / 1048576).toFixed(2)}MB)`);
            }
        });

        return errors;
    }

    /**
     * Convert base64 string to File object (CSP-safe, no fetch)
     */
    private convertBase64ToFile(base64: string, filename: string, mimeType: string): Promise<File> {
        return new Promise((resolve) => {
            // Decode base64 to binary
            const byteString = atob(base64);
            const arrayBuffer = new ArrayBuffer(byteString.length);
            const uint8Array = new Uint8Array(arrayBuffer);

            for (let i = 0; i < byteString.length; i++) {
                uint8Array[i] = byteString.charCodeAt(i);
            }

            // Create blob and file
            const blob = new Blob([uint8Array], { type: mimeType });
            const file = new File([blob], filename, { type: mimeType });

            resolve(file);
        });
    }

    loadProfileAcknowledgement(): void {
        this.systemConfigurationService
            .getSysConfigByKey('profile_acknowledgement')
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.profileAcknowledgement = response.data || '';
                },
                error: (error) => {
                    console.error('Failed to load profile acknowledgement:', error);
                    this.profileAcknowledgement = '';
                },
            });
    }

    onProfileAcknowledgementClick(event: MouseEvent): void {
        const anchor = (event.target as HTMLElement | null)?.closest('a');
        if (!anchor) {
            return;
        }

        const href = (anchor.getAttribute('href') || '').toLowerCase();

        if (href.includes('terms-and-conditions')) {
            event.preventDefault();
            window.open('/help/terms-and-conditions', '_blank', 'noopener,noreferrer');
            return;
        }

        if (href.includes('privacy')) {
            event.preventDefault();
            this.openDataPrivacy();
        }
    }

    openDataPrivacy() {
        if (this.isLoadingDataPrivacy) {
            return;
        }

        this.isLoadingDataPrivacy = true;
        this.systemConfigurationService
            .getSysConfigByKey('data_privacy')
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    const url = response.data || '#';
                    window.open(url, '_blank');
                    this.isLoadingDataPrivacy = false;
                },
                error: (error) => {
                    console.error('Failed to load privacy policy:', error);
                    this.isLoadingDataPrivacy = false;
                },
            });
    }

    @HostListener('window:resize', ['$event'])
    onResize() {
        this.checkMobile();
    }

    checkMobile() {
        // 1024px is the lg breakpoint in Tailwind
        this.isMobile = window.innerWidth < 1024;

        // If you want to use 768px (md breakpoint) instead:
        // this.isMobile = window.innerWidth < 768;
    }

    getInputWidth(): number | null {
        if (this.isMobile || !this.mirror?.nativeElement?.offsetWidth) {
            return null;
        }

        const mirrorWidth = this.mirror.nativeElement.offsetWidth;
        const calculatedWidth = Math.min(Math.max(mirrorWidth + 80, 100), 1000);
        return calculatedWidth;
    }

    getInputStyle(): any {
        if (this.isMobile) {
            return {
                'width.px': '100%',
                'text-overflow': 'ellipsis',
                'overflow': 'hidden',
                'white-space': 'nowrap'
            };
        }

        const width = this.getInputWidth();
        return width ? { 'width.px': width } : {};
    }

    /**
     * Cleans up subscriptions to prevent memory leaks.
     * @returns void
     */
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.investService.clearState();
    }
}
